/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
		Module No. 17, SBC, Thejaswini Building
		Phase 1, Technopark, Thiruvananthapuram - 695581, 
		Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd. (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/

import React, { useEffect, useState } from "react";
import { useTranslation } from 'react-i18next';
import { MediaPowderProductMasterService } from '../../../../../service/mediaPowderProductMaster';
import MediaPrepPageHeader from "../../../../common/commonComponents/mediaPrepPageHeader";
import MediaPrepHeaderSection from "../../../../common/commonComponents/mediaPrepheaderSection";

interface ProductMasterDetailsProps {
  productId: string;
  onBack: () => void;
}

const ProductMasterDetails: React.FC<ProductMasterDetailsProps> = ({ productId, onBack }) => {
  const [product, setProduct] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();

  useEffect(() => {
    const fetchProduct = async () => {
        setLoading(true);
        try {
        const response =
            await MediaPowderProductMasterService.getMediaProductById(productId);

        if (response?.data?.length > 0) {
            setProduct(response.data[0]); 
        } else {
            setProduct({});
        }
        } catch (error) {
        console.error("Error fetching product details", error);
        } finally {
        setLoading(false);
        }
    };

    if (productId) fetchProduct();
    }, [productId]);


  return (
    <div className="media-prep-scope">
      <MediaPrepHeaderSection />
      <MediaPrepPageHeader pageHeader={t('mediaPowderProductMaster.details.title')}  />
      {/* Sticky Header */}
      <div className="sticky top-[110px] z-40 bg-white border-b border-slate-200 px-10 py-3 flex items-center justify-between shadow-sm shrink-0">
        <div className="flex items-center gap-6">
          <button onClick={onBack} className="w-10 h-10 rounded-xl border border-slate-200 flex items-center justify-center text-slate-400 hover:text-[#FF7043] hover:bg-orange-50 transition-all">
            <span className="material-symbols-outlined text-xl">{t('mediaPowderProductMaster.icons.arrow_back')}</span>
          </button>
          <div>
            <div className="flex items-center gap-3">
              <h2 className="text-xl font-black text-[#2D3E50] tracking-tight uppercase">{t('mediaPowderProductMaster.details.pageTitle')}: {product?.media_name || "-"}</h2>

            </div>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">{t('mediaPowderProductMaster.details.ProductID')}: {product?.media_product_name || "-"}</p>
          </div>
        </div>
        {/* <div className="flex items-center gap-4">
           <button className="flex items-center gap-2 px-6 py-2.5 bg-[#2D3E50] text-white rounded-xl font-black text-[11px] uppercase tracking-widest hover:brightness-110 shadow-lg shadow-slate-200 transition-all">
             <span className="material-symbols-outlined text-sm">{t('mediaPowderProductMaster.icons.edit')}</span>
             {t('mediaPowderProductMaster.details.ModifySpecification')}
           </button>
        </div> */}
      </div>

      <div className="flex-1">
        <div className="p-10 max-w-5xl mx-auto space-y-10 pb-32">
          
          {/* 1. Core Identity & Manufacturer Details */}
          <section className="bg-white rounded-[2rem] border border-slate-200 shadow-sm overflow-hidden animate-in fade-in duration-500">
            <div className="px-10 py-2 border-b border-slate-100 bg-slate-50/30 flex items-center gap-3">
              <div className="w-10 h-10 rounded-2xl bg-orange-100/50 flex items-center justify-center text-[#FF7043]">
                <span className="material-symbols-outlined font-black">{t('mediaPowderProductMaster.icons.corporate_fare')}</span>
              </div>
              <h3 className="text-xs font-black text-[#2D3E50] uppercase tracking-widest mt-2">{t('mediaPowderProductMaster.details.supplierAndProduct')}</h3>
            </div>
            <div className="p-10 grid grid-cols-3 gap-12">
              <div className="space-y-1.5">
                <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">{t('mediaPowderProductMaster.details.manufacturer')}</p>
                <p className="text-base font-black text-slate-700">{product?.manufacturer_name || "-"}</p>
              </div>
              <div className="space-y-1.5">
                <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">{t('mediaPowderProductMaster.details.catalogNumber')}</p>
                <p className="text-base font-black text-[#FF7043] font-mono tracking-tight">{product?.catalog_number || "-"}</p>
              </div>
            </div>
          </section>

          {/* 2. Technical Physicochemical Parameters */}
          <section className="bg-white rounded-[2rem] border border-slate-200 shadow-sm overflow-hidden animate-in fade-in duration-500 delay-75">
            <div className="px-10 py-2 border-b border-slate-100 bg-slate-50/30 flex items-center gap-3">
              <div className="w-10 h-10 rounded-2xl bg-blue-100/50 flex items-center justify-center text-blue-600">
                <span className="material-symbols-outlined font-black">{t('mediaPowderProductMaster.icons.biotech')}</span>
              </div>
              <h3 className="text-xs font-black text-[#2D3E50] uppercase tracking-widest mt-2">{t('mediaPowderProductMaster.details.physicoChemical')}</h3>
            </div>
            <div className="px-10 py-4 grid grid-cols-2 gap-0">
              <div className="space-y-4">
                 <div className="flex items-center gap-8 pb-4 border-b border-slate-50">
                    <p className="w-30 text-[10px] font-black text-slate-400 uppercase tracking-widest whitespace-nowrap">{t('mediaPowderProductMaster.details.initialPh')}</p>
                    <div className="flex items-center gap-3">
                       <span className="text-xl font-black text-slate-800 mb-3">{product?.initial_ph || "-"}</span>
                       <span className="px-2 py-0.5 bg-slate-100 rounded text-[10px] font-black text-slate-500 mb-3">± {product?.initial_ph_tolerance || "-"}</span>
                    </div>
                 </div>
                 <div className="flex items-center gap-8 pb-4 border-b border-slate-50">
                    <p className="w-30 text-[10px] font-black text-slate-400 uppercase tracking-widest whitespace-nowrap">{t('mediaPowderProductMaster.details.targetPh')}</p>
                    <div className="flex items-center gap-3">
                      <span className="text-xl font-black text-slate-800 mb-3">{product?.final_ph || "-"}</span>
                      <span className="px-2 py-0.5 bg-slate-100 rounded text-[10px] font-black text-slate-500 mb-3">± {product?.final_ph_tolerance || "-"}</span>
                    </div>
                 </div>
              </div>
              <div className="space-y-8">
                 <div className="flex items-center gap-8 pb-4 border-b border-slate-50">
                    <p className="w-30 text-[10px] font-black text-slate-400 uppercase tracking-widest whitespace-nowrap">{t('mediaPowderProductMaster.details.referenceTemp')}</p>
                    <div className="flex items-center gap-3">
                      <span className="text-xl font-black text-slate-800 mb-3">{product?.reference_temperature || "-"}</span>
                    </div>
                 </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default ProductMasterDetails;
